import { useEffect, useMemo, useState } from "react"
import isEqual from "lodash.isequal"

import { GrpcErrorType } from "common/helpers/fetcher"
import type {
  ProposalSecurityCheckQuery,
  ProposalSecurityCheckQueryVariables,
  ThreatAnalysis,
  TransactionSimulationV2,
} from "query/graphql"
import { ProposalSecurityCheckDocument, SimulationStatus } from "query/graphql"
import { isSecurityCheckSupportedInChain } from "web3/helpers/chains"
import { chainIdToChainReference } from "web3/helpers/transformers"
import { useQuery } from "query/hooks/useQuery"
import {
  SimulationResult,
  type SimulationResultType,
} from "proposal/components/simulation/SimulationResultBadge"
import { normalize } from "common/helpers/string"

type Values = {
  overallResult: SimulationResultType | null
  threatAnalysisResult?: SimulationResultType
  simulationsResult?: SimulationResultType
  simulations: TransactionSimulationV2[] | null
  threatAnalysis: ThreatAnalysis | null
  isSecurityCheckSupported: boolean
  isLoading: boolean
  isError: boolean
  createdAt?: string
}

export const useProposalSimulation = ({
  proposalId,
  chainId,
}: {
  proposalId: string
  chainId: string
}): Values => {
  const isSecurityCheckSupported = isSecurityCheckSupportedInChain(
    chainIdToChainReference(chainId),
  )
  const [simulations, setSimulations] = useState<
    TransactionSimulationV2[] | null
  >(null)
  const [threatAnalysis, setThreatAnalysis] = useState<ThreatAnalysis | null>(
    null,
  )

  const { isLoading, data, isError } = useQuery<
    ProposalSecurityCheckQuery,
    ProposalSecurityCheckQueryVariables
  >(
    {
      query: ProposalSecurityCheckDocument,
      variables: {
        proposalId,
      },
      omittedErrors: [GrpcErrorType.NotFound],
    },
    { enabled: isSecurityCheckSupported },
  )

  useEffect(() => {
    if (!isLoading && data?.proposalSecurityCheck) {
      const proposalSecurityCheckMetadata =
        data?.proposalSecurityCheck?.metadata?.metadata ?? null

      const proposalSecurityCheckSimulations =
        data?.proposalSecurityCheck?.metadata?.simulations ?? null

      setThreatAnalysis(proposalSecurityCheckMetadata?.threatAnalysis ?? null)
      setSimulations(proposalSecurityCheckSimulations)
    } else {
      setSimulations(null)
      setThreatAnalysis(null)
    }
  }, [data, isLoading])

  // Hexagate
  const threatAnalysisResult = useMemo(() => {
    const threatAnalysisEvents =
      threatAnalysis?.actionsData?.flatMap(
        (actionsData) =>
          actionsData?.events?.filter((event) => event.severity !== "None") ??
          [],
      ) ?? []

    const hasThreatAnalysisError = threatAnalysisEvents.length > 0

    return hasThreatAnalysisError
      ? SimulationResult.Failed
      : SimulationResult.Success
  }, [threatAnalysis])

  // Tenderly
  const simulationsResult = useMemo(() => {
    if (!simulations) return undefined

    const someSimulationFailed =
      simulations?.some(
        (simulation) => simulation.result === SimulationStatus.Failed,
      ) ?? false

    if (someSimulationFailed) {
      return SimulationResult.Failed
    }

    // TODO(@nicolas): The "unknown" string should be replaced to SimulationStatus.Unknown
    // when this state is available in the API
    const someSimulationUnknown =
      simulations?.some((simulation) =>
        isEqual(normalize(simulation.result), normalize("unknown")),
      ) ?? false

    if (someSimulationUnknown) {
      return SimulationResult.Warning
    }

    return SimulationResult.Success
  }, [simulations])

  const overallResult = useMemo(() => {
    // If simulationsResult is null, then we consider the Simulation doesn't exist on this proposal
    if (!simulationsResult) return null

    if (threatAnalysisResult === "failed" || simulationsResult === "failed") {
      return SimulationResult.Failed
    }

    if (simulationsResult === "warning") {
      return SimulationResult.Warning
    }

    return SimulationResult.Success
  }, [threatAnalysisResult, simulationsResult])

  return {
    overallResult,
    simulations,
    threatAnalysisResult,
    simulationsResult,
    threatAnalysis,
    isLoading,
    isError,
    isSecurityCheckSupported,
    createdAt: data?.proposalSecurityCheck?.createdAt ?? undefined,
  }
}
